import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { InvestmentCalculator } from '@/lib/investment-calculator'
import type { InvestmentFormData, InvestmentCalculation } from '@/types/investment'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Checkbox } from '@/components/ui/checkbox'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { toast } from 'sonner'
import { 
  Calculator, 
  User, 
  Building, 
  DollarSign, 
  Calendar,
  AlertCircle,
  TrendingDown
} from 'lucide-react'

interface InvestmentFormProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  fundingRoundId: string
  projectId: string
  pricePerShare?: number
  onSuccess?: () => void
}

export default function InvestmentForm({
  open,
  onOpenChange,
  fundingRoundId,
  projectId,
  pricePerShare: defaultPricePerShare,
  onSuccess
}: InvestmentFormProps) {
  const queryClient = useQueryClient()
  const [investorType, setInvestorType] = useState<'internal' | 'external'>('external')
  const [calculation, setCalculation] = useState<InvestmentCalculation | null>(null)
  const [calculating, setCalculating] = useState(false)

  const [formData, setFormData] = useState<InvestmentFormData>({
    investor_type: 'vc',
    investment_amount: 0,
    equity_class_id: '',
    price_per_share: defaultPricePerShare || 0,
    is_lead_investor: false,
    investment_date: new Date().toISOString().split('T')[0]
  })

  // Fetch funding round details
  const { data: fundingRound } = useQuery({
    queryKey: ['funding-round', fundingRoundId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('funding_rounds')
        .select('*')
        .eq('id', fundingRoundId)
        .single()

      if (error) throw error
      return data
    }
  })

  // Fetch equity classes
  const { data: equityClasses } = useQuery({
    queryKey: ['equity-classes', projectId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('equity_classes')
        .select('*')
        .eq('project_id', projectId)
        .in('class_type', ['preferred', 'common'])
        .order('seniority_rank')

      if (error) throw error
      return data || []
    }
  })

  // Fetch internal users for investor selection
  const { data: internalUsers } = useQuery({
    queryKey: ['internal-users', projectId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('project_members')
        .select(`
          user_id,
          users!inner (
            id,
            first_name,
            last_name,
            email
          )
        `)
        .eq('project_id', projectId)

      if (error) throw error
      return data?.map(pm => pm.users) || []
    },
    enabled: investorType === 'internal'
  })

  // Set default equity class
  useEffect(() => {
    if (equityClasses && equityClasses.length > 0 && !formData.equity_class_id) {
      // Default to first preferred class, or first class if no preferred
      const preferredClass = equityClasses.find(ec => ec.class_name?.toLowerCase().includes('preferred'))
      setFormData(prev => ({
        ...prev,
        equity_class_id: preferredClass?.id || equityClasses[0].id
      }))
    }
  }, [equityClasses])

  // Set price per share from funding round if not provided
  useEffect(() => {
    if (fundingRound && !defaultPricePerShare && fundingRound.pre_money_valuation) {
      // No share_price on funding_rounds - would need to calculate
    }
  }, [fundingRound, defaultPricePerShare])

  // Calculate investment details when amount changes
  useEffect(() => {
    const calculateDetails = async () => {
      if (formData.investment_amount > 0 && formData.price_per_share > 0) {
        setCalculating(true)
        try {
          const details = await InvestmentCalculator.calculateInvestmentDetails(
            projectId,
            fundingRoundId,
            formData.investment_amount,
            formData.price_per_share
          )
          setCalculation(details)
        } catch (error) {
          console.error('Error calculating investment details:', error)
        } finally {
          setCalculating(false)
        }
      } else {
        setCalculation(null)
      }
    }

    const debounceTimer = setTimeout(calculateDetails, 500)
    return () => clearTimeout(debounceTimer)
  }, [formData.investment_amount, formData.price_per_share, projectId, fundingRoundId])

  // Create investment mutation
  const { mutate: createInvestment, isPending } = useMutation({
    mutationFn: async (data: InvestmentFormData) => {
      // Validate share authorization
      if (calculation) {
        const validation = await InvestmentCalculator.validateShareAuthorization(
          projectId,
          data.equity_class_id,
          calculation.sharesIssued
        )

        if (!validation.valid) {
          throw new Error(validation.message)
        }
      }

      const { error } = await supabase
        .from('investments')
        .insert({
          funding_round_id: fundingRoundId,
          investor_id: data.investor_id,
          investor_name: data.investor_name,
          investor_email: data.investor_email,
          investor_type: data.investor_type,
          investment_amount: data.investment_amount,
          equity_class_id: data.equity_class_id,
          shares_purchased: calculation?.sharesIssued || 0,
          price_per_share: data.price_per_share,
          is_lead_investor: data.is_lead_investor,
          investment_date: data.investment_date,
          wire_received_date: data.wire_received_date,
          investment_terms: (data.investment_terms || {}) as unknown as import('@/database.types').Json,
          notes: data.notes
        })

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['investments', fundingRoundId] })
      queryClient.invalidateQueries({ queryKey: ['funding-round', fundingRoundId] })
      queryClient.invalidateQueries({ queryKey: ['cap-table', projectId] })
      toast.success('Investment recorded successfully')
      onOpenChange(false)
      onSuccess?.()
    },
    onError: (error: Error) => {
      toast.error(error.message)
    }
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    createInvestment(formData)
  }

  const updateFormData = (updates: Partial<InvestmentFormData>) => {
    setFormData(prev => ({ ...prev, ...updates }))
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Record Investment</DialogTitle>
            <DialogDescription>
              Add a new investment to {fundingRound?.round_name}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Investor Type Selection */}
            <Tabs value={investorType} onValueChange={(v) => setInvestorType(v as any)}>
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="external">
                  <Building className="h-4 w-4 mr-2" />
                  External Investor
                </TabsTrigger>
                <TabsTrigger value="internal">
                  <User className="h-4 w-4 mr-2" />
                  Internal Investor
                </TabsTrigger>
              </TabsList>

              <TabsContent value="external" className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="investor_name">Investor Name</Label>
                    <Input
                      id="investor_name"
                      value={formData.investor_name || ''}
                      onChange={(e) => updateFormData({ 
                        investor_name: e.target.value,
                        investor_id: null 
                      })}
                      placeholder="Acme Ventures"
                      required={investorType === 'external'}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="investor_email">Investor Email</Label>
                    <Input
                      id="investor_email"
                      type="email"
                      value={formData.investor_email || ''}
                      onChange={(e) => updateFormData({ investor_email: e.target.value })}
                      placeholder="contact@acmeventures.com"
                      required={investorType === 'external'}
                    />
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="internal" className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="internal_investor">Select Investor</Label>
                  <Select
                    value={formData.investor_id || ''}
                    onValueChange={(value) => updateFormData({ 
                      investor_id: value,
                      investor_name: undefined,
                      investor_email: undefined
                    })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select an internal investor" />
                    </SelectTrigger>
                    <SelectContent>
                      {internalUsers?.map((user) => (
                        <SelectItem key={user.id} value={user.id}>
                          {user.first_name} {user.last_name} ({user.email})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </TabsContent>
            </Tabs>

            {/* Investment Details */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="investor_type">Investor Type</Label>
                <Select
                  value={formData.investor_type}
                  onValueChange={(value: any) => updateFormData({ investor_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="vc">Venture Capital</SelectItem>
                    <SelectItem value="angel">Angel Investor</SelectItem>
                    <SelectItem value="strategic">Strategic Investor</SelectItem>
                    <SelectItem value="employee">Employee</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="equity_class">Equity Class</Label>
                <Select
                  value={formData.equity_class_id}
                  onValueChange={(value) => updateFormData({ equity_class_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select equity class" />
                  </SelectTrigger>
                  <SelectContent>
                    {equityClasses?.map((ec) => (
                      <SelectItem key={ec.id} value={ec.id}>
                        {ec.class_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="investment_amount">Investment Amount ($)</Label>
                <Input
                  id="investment_amount"
                  type="number"
                  min="0"
                  step="1000"
                  value={formData.investment_amount || ''}
                  onChange={(e) => updateFormData({ 
                    investment_amount: parseFloat(e.target.value) || 0 
                  })}
                  placeholder="500000"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="price_per_share">Price per Share ($)</Label>
                <Input
                  id="price_per_share"
                  type="number"
                  min="0"
                  step="0.0001"
                  value={formData.price_per_share || ''}
                  onChange={(e) => updateFormData({ 
                    price_per_share: parseFloat(e.target.value) || 0 
                  })}
                  placeholder="10.00"
                  required
                />
              </div>
            </div>

            {/* Calculation Results */}
            {calculation && (
              <Alert>
                <Calculator className="h-4 w-4" />
                <AlertDescription>
                  <div className="space-y-2 mt-2">
                    <div className="flex justify-between">
                      <span>Shares to be issued:</span>
                      <span className="font-medium">
                        {calculation.sharesIssued.toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Ownership percentage:</span>
                      <span className="font-medium">
                        {InvestmentCalculator.formatPercentage(calculation.ownershipPercentage)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Post-money valuation:</span>
                      <span className="font-medium">
                        {InvestmentCalculator.formatCurrency(calculation.postMoney)}
                      </span>
                    </div>
                  </div>
                </AlertDescription>
              </Alert>
            )}

            {/* Dilution Preview */}
            {calculation && calculation.dilutionImpact.length > 0 && (
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <TrendingDown className="h-4 w-4" />
                  Dilution Impact (Top 5)
                </Label>
                <div className="border rounded-lg p-4 space-y-2 bg-gray-50">
                  {calculation.dilutionImpact.slice(0, 5).map((impact) => (
                    <div key={impact.shareholderId} className="flex justify-between text-sm">
                      <span>{impact.shareholderName}</span>
                      <span className="text-red-600">
                        {InvestmentCalculator.formatPercentage(impact.previousPercentage)} → {InvestmentCalculator.formatPercentage(impact.newPercentage)}
                        <span className="text-gray-500 ml-2">
                          (-{InvestmentCalculator.formatPercentage(impact.dilutionPercentage)})
                        </span>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="investment_date">Investment Date</Label>
                <Input
                  id="investment_date"
                  type="date"
                  value={formData.investment_date}
                  onChange={(e) => updateFormData({ investment_date: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="wire_received_date">Wire Received Date</Label>
                <Input
                  id="wire_received_date"
                  type="date"
                  value={formData.wire_received_date || ''}
                  onChange={(e) => updateFormData({ wire_received_date: e.target.value })}
                />
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="is_lead_investor"
                checked={formData.is_lead_investor}
                onCheckedChange={(checked) => updateFormData({ 
                  is_lead_investor: checked as boolean 
                })}
              />
              <Label 
                htmlFor="is_lead_investor" 
                className="text-sm font-normal cursor-pointer"
              >
                This is the lead investor for this round
              </Label>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes || ''}
                onChange={(e) => updateFormData({ notes: e.target.value })}
                placeholder="Additional notes about this investment..."
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={
                isPending || 
                calculating ||
                !formData.investment_amount ||
                !formData.price_per_share ||
                !formData.equity_class_id ||
                (investorType === 'external' && (!formData.investor_name || !formData.investor_email)) ||
                (investorType === 'internal' && !formData.investor_id)
              }
            >
              {isPending ? 'Recording...' : 'Record Investment'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}